import React, { useState } from 'react';
import { useAuth } from '../../../AuthContext';
import { useNavigate } from 'react-router-dom';
import { isErrorResponse } from '../../../types/ErrorResponse';
import { PasswordInput } from '../../../shared/PasswordInput';

type FormMode = 'login' | 'forgot' | 'confirmCode';

export const LoginForm: React.FC = () => {
  const [mode, setMode] = useState<FormMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, forgotPassword, confirmResetCode } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      setSubmitting(true);
      await login(email, password);
      navigate('/chat');
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setError(err.message);
      } else {
        setError('Login failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      setSubmitting(true);
      await forgotPassword(email);
      setMessage('If an account exists with this email, a password reset code has been sent.');
      setMode('confirmCode');
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setError(err.message);
      } else {
        setError('Failed to send reset code');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      setSubmitting(true);
      await confirmResetCode(email, code);
      navigate('/chat');
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setError(err.message);
      } else {
        setError('Invalid or expired code');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    setMode('login');
    setCode('');
    setError('');
    setMessage('');
  };

  const handleResendCode = async () => {
    setError('');
    setMessage('');
    try {
      setSubmitting(true);
      await forgotPassword(email);
      setMessage('A new code has been sent to your email.');
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setError(err.message);
      } else {
        setError('Failed to resend code');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (mode === 'forgot') {
    return (
      <form onSubmit={handleForgotPassword} className="login-form">
        <h2 className="h4 mb-3">Forgot Password</h2>
        <p className="text-muted mb-3">
          Enter your email address and we'll send you a code to reset your password.
        </p>
        <div className="mb-3">
          <label htmlFor="forgotEmail" className="form-label">
            Email
          </label>
          <input
            id="forgotEmail"
            type="email"
            className="form-control"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}
        {message && (
          <div className="alert alert-info py-2" role="alert">
            {message}
          </div>
        )}
        <button type="submit" className="btn btn-primary w-100 mb-2" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send Code'}
        </button>
        <button type="button" className="btn btn-link w-100" onClick={handleBackToLogin}>
          Back to Login
        </button>
      </form>
    );
  }

  if (mode === 'confirmCode') {
    return (
      <form onSubmit={handleConfirmCode} className="login-form">
        <h2 className="h4 mb-3">Enter Reset Code</h2>
        <p className="text-muted mb-3">Enter the 6-digit code sent to {email}</p>
        <div className="mb-3">
          <label htmlFor="resetCode" className="form-label">
            Reset Code
          </label>
          <input
            id="resetCode"
            type="text"
            className="form-control"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
            pattern="\d{6}"
            required
          />
        </div>
        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}
        {message && (
          <div className="alert alert-info py-2" role="alert">
            {message}
          </div>
        )}
        <button type="submit" className="btn btn-primary w-100 mb-2" disabled={submitting}>
          {submitting ? 'Confirming…' : 'Confirm Code'}
        </button>
        <button
          type="button"
          className="btn btn-secondary w-100 mb-2"
          onClick={handleResendCode}
          disabled={submitting}
        >
          Resend Code
        </button>
        <button type="button" className="btn btn-link w-100" onClick={handleBackToLogin}>
          Back to Login
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="login-form">
      <h2 className="h4 mb-3">Login</h2>
      <div className="mb-3">
        <label htmlFor="loginEmail" className="form-label">
          Email
        </label>
        <input
          id="loginEmail"
          type="email"
          className="form-control"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <PasswordInput
        id="loginPassword"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
        required
      />
      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}
      <button type="submit" className="btn btn-primary w-100 mb-2" disabled={submitting}>
        {submitting ? 'Logging in…' : 'Login'}
      </button>
      <button
        type="button"
        className="btn btn-link w-100 text-decoration-none"
        onClick={() => setMode('forgot')}
      >
        Forgot password?
      </button>
      <div className="text-center text-muted small mt-3" aria-hidden="true">
        Version: {process.env.REACT_APP_VERSION || 'dev'}
      </div>
    </form>
  );
};

export default LoginForm;
